// Read an answer aloud using the browser's own speech engine.
//
// Everything here runs on the device. No audio, no text and no request leaves
// the browser, which is why this uses speechSynthesis rather than a server.
//
// Two browser quirks drive the shape of this file:
//   - getVoices() is empty until the engine has loaded, hence the
//     voiceschanged listener.
//   - iOS Safari refuses to speak unless speak() is reached inside the same
//     user gesture that started it. So speakText() does no awaiting - it is
//     synchronous from the click handler down to speak().

export function isSpeechSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

// Longest single utterance we hand to the engine. There is no cap on the
// answer itself - long answers are split into chunks of about this size and
// queued. Chrome stops speaking a single long utterance after roughly fifteen
// seconds and never fires its end event; short utterances avoid that entirely.
const CHUNK_CHARS = 220;

let cachedVoice = null;

function pickVoice() {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || !voices.length) return null;

  // Indian English first, then any English, then whatever exists.
  cachedVoice =
    voices.find((v) => v.lang === "en-IN") ||
    voices.find((v) => (v.lang || "").startsWith("en")) ||
    voices[0];
  return cachedVoice;
}

if (isSpeechSupported()) {
  // Fires once the engine has its voice list. Without this the first click
  // gets no voice and falls back to the platform default.
  window.speechSynthesis.addEventListener("voiceschanged", () => {
    cachedVoice = null;
    pickVoice();
  });
}

const UNITS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen",
];
const TENS = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty",
  "ninety",
];

function twoDigits(n) {
  if (n < 20) return UNITS[n];
  const t = TENS[Math.floor(n / 10)];
  const u = n % 10;
  return u ? t + "-" + UNITS[u] : t;
}

// Indian numbering, because that is how these figures are read aloud here.
// 487215708 -> "forty-eight crore seventy-two lakh"
//
// Deliberately drops anything below a lakh: nobody wants five more words of
// rupees and paise read out after "forty-eight crore".
function indianWords(value) {
  const n = Math.round(Math.abs(value));
  if (n === 0) return "zero";

  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;

  const parts = [];
  if (crore) parts.push(twoDigits(crore % 100) + " crore");
  if (lakh) parts.push(twoDigits(lakh) + " lakh");
  // Below a lakh the smaller units still matter, above it they are noise.
  if (!crore && !lakh) {
    if (thousand) parts.push(twoDigits(thousand) + " thousand");
    if (rest) parts.push(String(rest));
  } else if (!crore && thousand) {
    parts.push(twoDigits(thousand) + " thousand");
  }
  return parts.join(" ");
}

// Turn money into words. Digit-by-digit is the default behaviour of every
// speech engine and it is unusable for figures this size.
//
// Two shapes occur in answers and both must be consumed whole:
//   ₹48,72,15,708   raw rupees
//   ₹3.28 crore     already scaled, sometimes written "Cr" or "lakh"
// Matching only the digits leaves the unit behind, which then gets read as
// "three rupees dot twenty-eight crore".
// The unit and the space before it are optional TOGETHER. Written as
// "\s*(unit)?" the space is swallowed even when no unit follows, which glues
// the next word on: "five thousand rupeesand one lakh".
const MONEY = /₹\s*([\d,]*\d(?:\.\d+)?)(?:\s*(crores?|cr|lakhs?|lacs?))?/gi;

const SCALE = { crore: 10000000, crores: 10000000, cr: 10000000,
                lakh: 100000, lakhs: 100000, lac: 100000, lacs: 100000 };

function humaniseMoney(text) {
  return text.replace(MONEY, (whole, digits, unit) => {
    const base = Number(digits.replace(/,/g, ""));
    if (!Number.isFinite(base) || base === 0) return whole;

    const scale = unit ? SCALE[unit.toLowerCase()] || 1 : 1;
    const n = Math.round(base * scale);

    if (n < 1000) return n + " rupees";
    return indianWords(n) + " rupees";
  });
}

// Strip the things that are fine on screen and wrong out loud.
function toSpoken(markdown) {
  let t = String(markdown || "");

  t = t.replace(/```[\s\S]*?```/g, " ");        // fenced code / SQL
  t = t.replace(/^\s*\|.*\|\s*$/gm, " ");       // table rows
  t = t.replace(/^\s*[-:|\s]+$/gm, " ");        // table separator rows
  t = t.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1"); // links, keep the label
  t = t.replace(/[*_`#>]/g, "");                // emphasis, headings, quotes
  t = t.replace(/^\s*[-•]\s+/gm, ", ");         // bullets become pauses

  // Answers often print a figure twice - "₹3.45 crore (₹3,45,18,285)". Spoken,
  // that is the same number read out twice in a row. Keep the readable form
  // and drop the bracketed exact one; the screen still shows both.
  t = t.replace(
    /(₹\s*[\d,.]+\s*(?:crores?|cr|lakhs?|lacs?))\s*\(\s*₹[^)]*\)/gi,
    "$1",
  );

  t = humaniseMoney(t);

  return t.replace(/\s+/g, " ").trim();
}

// Split into utterances the engine will finish. Sentence boundaries first, so
// the pauses land where a reader would pause; only an unusually long sentence
// gets broken at a comma or a space.
function toChunks(text) {
  const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || [text];
  const chunks = [];
  let current = "";

  const push = (s) => {
    const piece = s.trim();
    if (piece) chunks.push(piece);
  };

  for (const sentence of sentences) {
    if ((current + sentence).length <= CHUNK_CHARS) {
      current += sentence;
      continue;
    }
    push(current);
    current = "";

    if (sentence.length <= CHUNK_CHARS) {
      current = sentence;
      continue;
    }
    // One very long sentence - break it on commas, then on spaces.
    let rest = sentence;
    while (rest.length > CHUNK_CHARS) {
      const window = rest.slice(0, CHUNK_CHARS);
      let at = window.lastIndexOf(", ");
      if (at < CHUNK_CHARS / 3) at = window.lastIndexOf(" ");
      if (at <= 0) at = CHUNK_CHARS;
      push(rest.slice(0, at + 1));
      rest = rest.slice(at + 1);
    }
    current = rest;
  }
  push(current);
  return chunks;
}

// Identifies the current run. stopSpeaking() and any later run bump this, so
// an onend arriving late from a cancelled run cannot clear the new one's
// button.
let runId = 0;

/**
 * Speak an answer. Safe to call with raw markdown.
 *
 * Call this DIRECTLY from a click handler - do not await anything first, or
 * iOS will silently refuse.
 *
 * onDone runs when the whole answer has been read, or when speaking stops for
 * any other reason. It never runs for a run that was superseded.
 *
 * Returns true if speech was started.
 */
export function speakText(markdown, onDone) {
  if (!isSpeechSupported()) return false;

  const text = toSpoken(markdown);
  if (!text) return false;

  const chunks = toChunks(text);
  if (!chunks.length) return false;

  try {
    // Starting a new answer replaces whatever was being read.
    window.speechSynthesis.cancel();
    const myRun = ++runId;

    const finish = () => {
      if (myRun === runId && typeof onDone === "function") onDone();
    };

    const voice = pickVoice();
    chunks.forEach((chunk, index) => {
      const utterance = new window.SpeechSynthesisUtterance(chunk);
      utterance.lang = "en-IN";
      utterance.rate = 1;
      if (voice) utterance.voice = voice;

      if (index === chunks.length - 1) utterance.onend = finish;
      // A failed chunk ends the run rather than leaving the button stuck on
      // stop with nothing playing.
      utterance.onerror = finish;

      window.speechSynthesis.speak(utterance);
    });
    return true;
  } catch {
    // Never let a speech failure take the message list down with it.
    return false;
  }
}

/** Stop anything currently being read, including chunks still queued. */
export function stopSpeaking() {
  if (!isSpeechSupported()) return;
  runId += 1; // any onend still in flight belongs to a dead run
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* nothing to do */
  }
}

export function isSpeaking() {
  if (!isSpeechSupported()) return false;
  try {
    return window.speechSynthesis.speaking;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Recording, for speech to text
// ---------------------------------------------------------------------------
//
// The clip is sent to the agent service, which transcribes it. The browser's
// own SpeechRecognition is deliberately not used: it streams audio to Google,
// and it does not exist on iOS.

export function isRecordingSupported() {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  );
}

// Hard stop, so a button left held - or a page left open - cannot record
// forever. The service refuses anything much longer than this anyway.
const MAX_RECORD_MS = 30000;

/**
 * Start recording. Resolves with a Blob when stop() is called.
 *
 * Returns { stop, cancel } - stop() ends the clip and settles the promise,
 * cancel() discards it. The microphone track is released either way.
 */
export async function startRecording({ onBlob, onError } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  // Let the browser pick its own container. Chromium gives webm/opus, iOS
  // gives mp4/aac; both are accepted upstream, so forcing a mimeType only
  // risks an unsupported-type error on one platform or the other.
  const recorder = new window.MediaRecorder(stream);
  const parts = [];
  let cancelled = false;
  let timer = null;

  const release = () => {
    if (timer) clearTimeout(timer);
    stream.getTracks().forEach((t) => t.stop());
  };

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) parts.push(e.data);
  };

  recorder.onstop = () => {
    release();
    if (cancelled) return;
    const blob = new Blob(parts, { type: recorder.mimeType || "audio/webm" });
    if (blob.size && typeof onBlob === "function") onBlob(blob);
  };

  recorder.onerror = (e) => {
    release();
    if (typeof onError === "function") onError(e?.error || new Error("Recording failed"));
  };

  recorder.start();
  timer = setTimeout(() => {
    if (recorder.state === "recording") recorder.stop();
  }, MAX_RECORD_MS);

  return {
    stop() {
      if (recorder.state === "recording") recorder.stop();
      else release();
    },
    cancel() {
      cancelled = true;
      if (recorder.state === "recording") recorder.stop();
      else release();
    },
  };
}
